import dayjs from 'dayjs';
import type { AnswerFormat, PreparedFileValue, TaskFormat } from 'models/task';
import {
  isFile,
  isImageFile,
  isObjWithOriginFileObj,
  isObjWithThumbnail,
  isVideoFile,
} from 'utils/helpers/file';

import { api } from '..';
import type {
  CloneTasksArgs,
  CreateAnswerArgs,
  CreateCommentArgs,
  CreateTaskArgs,
  DeleteTasksArgs,
  GetAnswersArgs,
  GetAnswersResponse,
  GetAnswersResponseDto,
  GetCommentsArgs,
  GetCommentsResponse,
  GetTaskArgs,
  GetTaskResponse,
  GetTaskResponseDto,
  GetTasksArgs,
  GetTasksResponse,
  UpdateAnswerArgs,
  UpdateTaskArgs,
} from './types';
import type { ResponseOnlySuccess } from '../common';

export const taskManagerApi = api.injectEndpoints({
  endpoints: (build) => ({
    /* TASKS */
    getTasks: build.query<GetTasksResponse, GetTasksArgs>({
      query: (params) => ({
        url: `/tasks`,
        method: 'GET',
        params,
      }),
      providesTags: ['Tasks'],
    }),
    getTask: build.query<GetTaskResponse, GetTaskArgs>({
      query: ({ id }) => ({
        url: `/tasks/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: GetTaskResponseDto): GetTaskResponse => {
        const { data } = response;

        // if (isTaskSimple(data)) {
        const format = data.format.reduce<TaskFormat>(
          (acc, item) => {
            acc.fields[item.id] = item;
            acc.order.push(item.id);
            return acc;
          },
          { fields: {}, order: [] }
        );

        return { ...response, data: { ...data, format } };
        // }
      },

      providesTags: (result, _, { id }) => (result ? [{ type: 'Tasks', id }] : []),
    }),
    createTask: build.mutation<ResponseOnlySuccess, CreateTaskArgs>({
      query: (body) => ({
        url: '/tasks',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Tasks'],
    }),
    cloneTasks: build.mutation<ResponseOnlySuccess, CloneTasksArgs>({
      query: (body) => ({
        url: '/tasks/clone',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Tasks'],
    }),
    deleteTasks: build.mutation<ResponseOnlySuccess, DeleteTasksArgs>({
      query: (body) => ({
        url: '/tasks',
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['Tasks'],
    }),
    updateTask: build.mutation<ResponseOnlySuccess, UpdateTaskArgs>({
      query: ({ id, ...body }) => ({
        url: `/tasks/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) => (result ? [{ type: 'Tasks', id }] : []),
    }),

    /* ANSWERS */
    getAnswers: build.query<GetAnswersResponse, GetAnswersArgs>({
      query: ({ taskId, ...params }) => ({
        url: `/tasks/${taskId}/answers`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: GetAnswersResponseDto): GetAnswersResponse => {
        const { data } = response;

        const transformedData = data.map((answer) => {
          const format = answer.format.reduce<AnswerFormat>(
            (acc, item) => {
              acc.fields[item.id] = item;
              acc.order.push(item.id);
              return acc;
            },
            { fields: {}, order: [] }
          );

          return { ...answer, format };
        });

        return { ...response, data: transformedData };
      },

      providesTags: ['TasksAnswers'],
      keepUnusedDataFor: 60,
    }),
    createAnswer: build.mutation<ResponseOnlySuccess, CreateAnswerArgs>({
      query: ({ taskId, ...args }) => {
        const formData = new FormData();

        if (args?.optional?.text) {
          formData.append('optional-text', args.optional.text);
        }

        if (args?.optional?.files && args.optional.files.length > 0) {
          for (const file of args.optional.files) {
            let origFile: File | undefined = undefined;

            if (isObjWithOriginFileObj(file)) {
              origFile = file.originFileObj;
            } else if (isFile(file)) {
              origFile = file;
            }

            if (!origFile) continue;

            if (isVideoFile(origFile) && isObjWithThumbnail(origFile)) {
              formData.append(`optional-video-${file.uid}`, origFile, file.name);
              formData.append(
                `optional-video-preview-${file.uid}`,
                origFile.thumbFile,
                file.name.split('.').slice(0, -1).join('.') + '.preview.png'
              );
            } else if (isImageFile(origFile)) {
              formData.append(`optional-image-${file.uid}`, origFile, file.name);
            }
          }
        }

        const formatEntries = Object.entries(args.format ?? {});

        if (formatEntries.length > 0) {
          for (const [name, value] of formatEntries) {
            if (typeof value === 'string') {
              formData.append(`format-${name}`, value);
            }

            if (dayjs.isDayjs(value)) {
              formData.append(`format-${name}`, value.toDate().toISOString());
            }

            if (Array.isArray(value) && value.length > 0) {
              for (const file of value) {
                let origFile: File | undefined = undefined;

                if (isObjWithOriginFileObj(file)) {
                  origFile = file.originFileObj;
                } else if (isFile(file)) {
                  origFile = file;
                }

                if (!origFile) continue;

                if (isVideoFile(origFile) && isObjWithThumbnail(origFile)) {
                  formData.append(`format-video-${name}-${file.uid}`, origFile, file.name);
                  formData.append(
                    `format-video-preview-${name}-${file.uid}`,
                    origFile.thumbFile,
                    file.name.split('.').slice(0, -1).join('.') + '.preview.png'
                  );
                } else if (isImageFile(origFile)) {
                  formData.append(`format-image-${name}-${file.uid}`, origFile, file.name);
                }
              }
            }
          }
        }

        return {
          url: `/tasks/${taskId}/answers`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['TasksAnswers'],
    }),
    updateAnswer: build.mutation<ResponseOnlySuccess, UpdateAnswerArgs>({
      query: ({ taskId, answerId, ...args }) => {
        const formData = new FormData();

        formData.append('optional-text', args.optional.text);

        const optionalPreparedFileUrls: PreparedFileValue[] = [];

        for (const media of args.optional.files) {
          if ('url' in media) {
            optionalPreparedFileUrls.push(media);
          } else {
            let origFile: File | undefined = undefined;

            if (isObjWithOriginFileObj(media.file)) {
              origFile = media.file.originFileObj;
            } else if (isFile(media.file)) {
              origFile = media.file;
            }

            if (!origFile) continue;

            if (media.type === 'image') {
              formData.append(`optional-image-${media.file.uid}`, origFile, origFile.name);
            } else if (media.type === 'video' && isObjWithThumbnail(origFile)) {
              formData.append(`optional-video-${media.file.uid}`, origFile, origFile.name);
              formData.append(
                `optional-video-preview-${media.file.uid}`,
                origFile.thumbFile,
                origFile.name.split('.').slice(0, -1).join('.') + '.preview.png'
              );
            }
          }
        }

        formData.append('optional-files', JSON.stringify(optionalPreparedFileUrls));

        const formatPreparedFileUrls: Record<string, PreparedFileValue[]> = {};
        const formatEntries = Object.entries(args.format.fieldValues);

        if (formatEntries.length > 0) {
          for (const [field, value] of formatEntries) {
            if (typeof value === 'string') {
              formData.append(`format-text-${field}`, value);
            }

            if (dayjs.isDayjs(value)) {
              formData.append(`format-date-${field}`, value.toDate().toISOString());
            }

            if (Array.isArray(value)) {
              for (const media of value) {
                if (typeof media === 'object') {
                  if ('url' in media) {
                    if (!formatPreparedFileUrls[field]) {
                      formatPreparedFileUrls[field] = [];
                    }
                    formatPreparedFileUrls[field].push(media);
                  } else {
                    let origFile: File | undefined = undefined;

                    if (isObjWithOriginFileObj(media.file)) {
                      origFile = media.file.originFileObj;
                    } else if (isFile(media.file)) {
                      origFile = media.file;
                    }

                    if (!origFile) continue;

                    if (media.type === 'image') {
                      formData.append(`format-image-${field}-${media.file.uid}`, origFile, origFile.name);
                    } else if (media.type === 'video' && isObjWithThumbnail(origFile)) {
                      formData.append(`format-video-${field}-${media.file.uid}`, origFile, origFile.name);
                      formData.append(
                        `format-video-preview-${field}-${media.file.uid}`,
                        origFile.thumbFile,
                        origFile.name.split('.').slice(0, -1).join('.') + '.preview.png'
                      );
                    }
                  }
                }
              }
            }
          }
        }

        for (const [field, files] of Object.entries(formatPreparedFileUrls)) {
          formData.append(`format-files-${field}`, JSON.stringify(files));
        }

        return {
          url: `/tasks/${taskId}/answers/${answerId}`,
          method: 'PATCH',
          body: formData,
        };
      },
      invalidatesTags: ['TasksAnswers'],
    }),

    /* COMMENTS */
    getComments: build.query<GetCommentsResponse, GetCommentsArgs>({
      query: ({ taskId, id, targetType, ...params }) => ({
        url: `/tasks/${taskId}/${targetType}/${id}/comments`,
        method: 'GET',
        params,
      }),
      providesTags: (result, error, { taskId, id, targetType }) => {
        if (!result) return [{ type: 'TasksComments', id: 'LIST' }];
        return [{ type: 'TasksComments', id: `${taskId}-${targetType}-${id}` }];
      },
      keepUnusedDataFor: 60,
    }),
    createComment: build.mutation<ResponseOnlySuccess, CreateCommentArgs>({
      query: ({ taskId, userId, targetId, targetType, ...args }) => {
        const formData = new FormData();

        formData.append('targetType', targetType);

        if (args.text) {
          formData.append('text', args.text);
        }

        if (args.files && args.files.length > 0) {
          args.files.forEach((file) => file && formData.append(`file-${file.name}`, file));
        }

        return {
          url: `/tasks/${taskId}/${targetType}/${targetId}/comments`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { taskId, targetId, targetType }) => {
        if (!result) {
          return [];
        }

        if (targetType === 'answers') {
          return [{ type: 'TasksComments', id: `${taskId}-${targetType}-${targetId}` }, 'TasksAnswers'];
        }

        return [];
      },
    }),
  }),
});

export const {
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useCloneTasksMutation,
  useDeleteTasksMutation,
  useUpdateTaskMutation,

  useGetAnswersQuery,
  useCreateAnswerMutation,
  useUpdateAnswerMutation,

  useGetCommentsQuery,
  useLazyGetCommentsQuery,
  useCreateCommentMutation,
} = taskManagerApi;
